/**
 * Gemeenschappen Service Layer
 * 
 * This module provides all database operations for the gemeenschappen collection.
 */

import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, serverTimestamp, getDoc, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase/config';

const COLLECTION_NAME = 'gemeenschappen';

const normalizeEmail = (value) => (value || '').trim().toLowerCase();
const LEDEN_SUBCOLLECTION = 'leden';

const buildToegestaneEmails = ({ verantwoordelijkeEmail, catechistEmail, createdBy, existing = [] }) => {
  const merged = [
    ...existing,
    verantwoordelijkeEmail,
    catechistEmail,
    createdBy
  ];

  return Array.from(
    new Set(
      merged
        .map((email) => normalizeEmail(email))
        .filter(Boolean)
    )
  );
};

/**
 * Retrieves all gemeenschappen from Firestore
 * Sorted by bisdom, then parochie, then gemeenschap
 * 
 * @returns {Promise<Array>} Array of gemeenschap objects
 */
export const getGemeenschappen = async () => {
  try {
    const gemeenschappenCollection = collection(db, COLLECTION_NAME);
    const querySnapshot = await getDocs(gemeenschappenCollection);
    
    const gemeenschappen = [];
    querySnapshot.forEach((doc) => {
      gemeenschappen.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by bisdom, then parochie, then gemeenschap
    gemeenschappen.sort((a, b) => {
      if (a.bisdom !== b.bisdom) return (a.bisdom || '').localeCompare(b.bisdom || '');
      if (a.parochie !== b.parochie) return (a.parochie || '').localeCompare(b.parochie || '');
      return (a.gemeenschap || '').localeCompare(b.gemeenschap || '');
    });
    
    return gemeenschappen;
  } catch (error) {
    console.error('Error getting gemeenschappen:', error);
    throw error;
  }
};

/**
 * Retrieves gemeenschappen where the user is verantwoordelijke, catechist, or creator (by email match)
 *
 * @param {string} userEmail - The user's email address
 * @returns {Promise<Array>} Array of gemeenschap objects where user has access
 */
export const getGemeenschappenForUser = async (userEmail) => {
  try {
    const gemeenschappenCollection = collection(db, COLLECTION_NAME);

    const normalizedEmail = (userEmail || '').trim().toLowerCase();
    if (!normalizedEmail) return [];

    // Single-query access model: gemeenschap doc contains a consolidated allow-list.
    const accessQuery = query(
      gemeenschappenCollection,
      where('toegestaneEmails', 'array-contains', normalizedEmail)
    );
    const querySnapshot = await getDocs(accessQuery);

    const gemeenschappen = [];
    querySnapshot.forEach((docSnapshot) => {
      gemeenschappen.push({
        id: docSnapshot.id,
        ...docSnapshot.data()
      });
    });

    // Sort by bisdom, then parochie, then gemeenschap
    gemeenschappen.sort((a, b) => {
      if (a.bisdom !== b.bisdom) return (a.bisdom || '').localeCompare(b.bisdom || '');
      if (a.parochie !== b.parochie) return (a.parochie || '').localeCompare(b.parochie || '');
      return (a.gemeenschap || '').localeCompare(b.gemeenschap || '');
    });

    return gemeenschappen;
  } catch (error) {
    console.error('Error getting gemeenschappen for user:', error);
    throw error;
  }
};

/**
 * Retrieves the gemeenschappen assigned to a user, split by role.
 *
 * Uses the single-query access model (getGemeenschappenForUser) and then
 * splits the result into "verantwoordelijke" and "catechist" buckets by
 * matching normalized email addresses. A gemeenschap can appear in both
 * lists when the user holds both roles there.
 *
 * @param {string} userEmail - The user's email address
 * @returns {Promise<{asVerantwoordelijke: Array, asCatechist: Array}>}
 */
export const getUserGemeenschappenByRole = async (userEmail) => {
  const gemeenschappen = await getGemeenschappenForUser(userEmail);
  const normalizedEmail = (userEmail || '').trim().toLowerCase();

  const asVerantwoordelijke = gemeenschappen.filter(
    (gemeenschap) => normalizeValue(gemeenschap.verantwoordelijkeEmail) === normalizedEmail
  );
  const asCatechist = gemeenschappen.filter(
    (gemeenschap) => normalizeValue(gemeenschap.catechistEmail) === normalizedEmail
  );

  return { asVerantwoordelijke, asCatechist };
};

const normalizeValue = (value) => (value || '').trim().toLowerCase();

const deleteLegacyGemeenschapLeden = async (gemeenschapId) => {
  try {
    const ledenCollection = collection(db, COLLECTION_NAME, gemeenschapId, LEDEN_SUBCOLLECTION);
    const querySnapshot = await getDocs(ledenCollection);

    const deletePromises = [];
    querySnapshot.forEach((docSnapshot) => {
      deletePromises.push(deleteDoc(docSnapshot.ref));
    });

    await Promise.all(deletePromises);
  } catch (error) {
    console.warn(`Could not cleanup legacy leden for gemeenschap ${gemeenschapId}:`, error);
  }
};

/**
 * Returns all unique bisdom values from gemeenschap records.
 *
 * @returns {Promise<string[]>}
 */
export const getUniqueBisdommen = async () => {
  const gemeenschappen = await getGemeenschappen();
  const uniqueBisdommen = new Set(
    gemeenschappen
      .map((item) => (item.bisdom || '').trim())
      .filter(Boolean)
  );

  return Array.from(uniqueBisdommen).sort((a, b) => a.localeCompare(b));
};

/**
 * Returns all unique parochie values for a given bisdom.
 *
 * @param {string} bisdom
 * @returns {Promise<string[]>}
 */
export const getParochiesForBisdomDynamic = async (bisdom) => {
  if (!bisdom) return [];

  const targetBisdom = normalizeValue(bisdom);
  const gemeenschappen = await getGemeenschappen();

  const uniqueParochies = new Set(
    gemeenschappen
      .filter((item) => normalizeValue(item.bisdom) === targetBisdom)
      .map((item) => (item.parochie || '').trim())
      .filter(Boolean)
  );

  return Array.from(uniqueParochies).sort((a, b) => a.localeCompare(b));
};

/**
 * Returns all unique gemeenschap names for a given bisdom and parochie.
 *
 * @param {string} bisdom
 * @param {string} parochie
 * @returns {Promise<string[]>}
 */
export const getGemeenschappenForParochieDynamic = async (bisdom, parochie) => {
  if (!bisdom || !parochie) return [];

  const targetBisdom = normalizeValue(bisdom);
  const targetParochie = normalizeValue(parochie);
  const gemeenschappen = await getGemeenschappen();

  const uniqueGemeenschappen = new Set(
    gemeenschappen
      .filter(
        (item) =>
          normalizeValue(item.bisdom) === targetBisdom &&
          normalizeValue(item.parochie) === targetParochie
      )
      .map((item) => (item.gemeenschap || '').trim())
      .filter(Boolean)
  );

  return Array.from(uniqueGemeenschappen).sort((a, b) => a.localeCompare(b));
};

/**
 * Finds a gemeenschap by location fields.
 *
 * @param {string} bisdom
 * @param {string} parochie
 * @param {string} gemeenschap
 * @returns {Promise<Object|null>}
 */
export const getGemeenschapByLocation = async (bisdom, parochie, gemeenschap) => {
  if (!bisdom || !parochie || !gemeenschap) return null;

  const targetBisdom = normalizeValue(bisdom);
  const targetParochie = normalizeValue(parochie);
  const targetGemeenschap = normalizeValue(gemeenschap);
  const gemeenschappen = await getGemeenschappen();

  const match = gemeenschappen.find(
    (item) =>
      normalizeValue(item.bisdom) === targetBisdom &&
      normalizeValue(item.parochie) === targetParochie &&
      normalizeValue(item.gemeenschap) === targetGemeenschap
  );

  return match || null;
};

/**
 * Adds a new gemeenschap to Firestore
 * 
 * @param {Object} gemeenschap - The gemeenschap data
 * @returns {Promise<Object>} The created gemeenschap with id
 */
export const addGemeenschap = async (gemeenschap) => {
  try {
    const user = auth.currentUser;
    const userEmail = user ? user.email : 'unknown';
    const createdByEmail = normalizeEmail(userEmail);
    const toegestaneEmails = buildToegestaneEmails({
      verantwoordelijkeEmail: gemeenschap.verantwoordelijkeEmail,
      catechistEmail: gemeenschap.catechistEmail,
      createdBy: createdByEmail,
      existing: gemeenschap.toegestaneEmails || []
    });
    
    const gemeenschapWithTimestamp = {
      ...gemeenschap,
      toegestaneEmails,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: createdByEmail,
      updatedBy: createdByEmail
    };
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), gemeenschapWithTimestamp);
    
    return {
      id: docRef.id,
      ...gemeenschapWithTimestamp,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error adding gemeenschap:', error);
    throw error;
  }
};

/**
 * Updates an existing gemeenschap
 * 
 * @param {string} id - The document ID
 * @param {Object} updates - The fields to update
 * @returns {Promise<Object>} The updated gemeenschap
 */
export const updateGemeenschap = async (id, updates) => {
  try {
    const user = auth.currentUser;
    const userEmail = user ? user.email : 'unknown';
    const normalizedUpdater = normalizeEmail(userEmail);

    const docRef = doc(db, COLLECTION_NAME, id);
    const snapshot = await getDoc(docRef);
    const existing = snapshot.exists() ? snapshot.data() : {};

    const createdBy = normalizeEmail(updates.createdBy || existing.createdBy || userEmail);
    const verantwoordelijkeEmail = updates.verantwoordelijkeEmail !== undefined
      ? updates.verantwoordelijkeEmail
      : existing.verantwoordelijkeEmail;
    const catechistEmail = updates.catechistEmail !== undefined
      ? updates.catechistEmail
      : existing.catechistEmail;

    const toegestaneEmails = buildToegestaneEmails({
      verantwoordelijkeEmail,
      catechistEmail,
      createdBy,
      existing: updates.toegestaneEmails || existing.toegestaneEmails || []
    });
    
    const updatesWithTimestamp = {
      ...updates,
      toegestaneEmails,
      updatedAt: serverTimestamp(),
      updatedBy: normalizedUpdater
    };

    await updateDoc(docRef, updatesWithTimestamp);
    
    return {
      id,
      ...updatesWithTimestamp,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error updating gemeenschap:', error);
    throw error;
  }
};

/**
 * Deletes a gemeenschap and all associated leden (cascade delete)
 * 
 * @param {string} id - The document ID
 * @returns {Promise<void>}
 */
export const deleteGemeenschap = async (id) => {
  try {
    // Cleanup legacy leden subcollection before deleting the gemeenschap document.
    await deleteLegacyGemeenschapLeden(id);
    
    // Delete the gemeenschap document
    const docRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting gemeenschap:', error);
    throw error;
  }
};
